import { createSelector } from '@reduxjs/toolkit'
import { NetSuiteAsset, NetSuiteAssetProduct, ContractLineDetails } from 'types'
import type { ContractLineAssetManagerState } from 'store/contractLineAssetManager/reducer'

export const selectNetSuiteAssetById = createSelector(
  [
    (state: ContractLineAssetManagerState) => state?.netSuiteAssets,
    (_state: ContractLineAssetManagerState, id: number) => id,
  ],
  (assets, id): NetSuiteAsset | undefined => (assets ? assets[id] : undefined),
)

export const selectAssetsByContractLineId = createSelector(
  [
    (state: ContractLineAssetManagerState) => state?.netSuiteAssets,
    (_state: ContractLineAssetManagerState, contractLineId: number) =>
      contractLineId,
  ],
  (assets, contractLineId): NetSuiteAsset[] => {
    if (!assets || !contractLineId) return []

    return Object.values(assets).filter(
      asset => asset.contractLineNSId === contractLineId,
    )
  },
)

export const selectContractLineDetails = createSelector(
  [
    selectAssetsByContractLineId,
    (state: ContractLineAssetManagerState) => state.setupAdminEmailUserId,
    (state: ContractLineAssetManagerState) => state.managerNetSuiteCustomerName,
    (state: ContractLineAssetManagerState) => state.managerNetSuiteCustomerNSId,
  ],
  (
    assets,
    setupAdminEmailUserId,
    managerNetSuiteCustomerName,
    managerNetSuiteCustomerNSId,
  ): ContractLineDetails => {
    if (!assets || assets.length === 0) {
      return {
        assignedAssets: [],
        unassignedAssets: [],
        products: [],
        contractLineId: undefined,
        managerNetSuiteCustomerId: undefined,
        startDate: undefined,
        expirationDate: undefined,
        adminEmail: null,
        setupAdminEmailUserId: null,
        managerNetSuiteCustomerName: null,
        managerNetSuiteCustomerNSId: null,
      }
    }

    const assignedAssets = assets.filter(asset => asset.site !== null)
    const unassignedAssets = assets.filter(asset => asset.site === null)

    const products = new Map<number, NetSuiteAssetProduct>()
    assets.forEach(asset => {
      if (asset.product && !products.has(asset.product.id)) {
        products.set(asset.product.id, asset.product)
      }
    })
    /**
     * IN CSS-TOOLS, we display a contract line summary with the first asset's information
     * Netsuite will always have all netsuite have same shared information, so adminEmail will be the same for all assets under that contract line,
     * start date and expiration date will be the same for all assets under that contract line,
     * managerNetSuiteCustomerI and payerNetSuiteCustomerId will also be the same for all assets under that contract line
     */

    const firstAsset = assets[0]

    return {
      assignedAssets,
      unassignedAssets,
      products: Array.from(products.values()),
      contractLineId: firstAsset.contractLineNSId,
      managerNetSuiteCustomerId: firstAsset.managerNetSuiteCustomerId,
      startDate: firstAsset.startDate,
      expirationDate: firstAsset.expirationDate,
      adminEmail: firstAsset.adminEmail,
      setupAdminEmailUserId,
      managerNetSuiteCustomerName,
      managerNetSuiteCustomerNSId,
    }
  },
)
